import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { ActivityService } from 'src/app/services/activity.service';
import { AssessmentService } from 'src/app/services/assessment.service';

import * as FilePond from 'filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';

@Component({
    selector: 'app-upload-activity-video',
    templateUrl: './upload-activity-video.component.html',
    styleUrl: './upload-activity-video.component.scss',
    standalone: false
})
export class UploadActivityVideoComponent implements OnInit, OnDestroy {
  @ViewChild('myPond') myPond!: any;
  @Input() projectId!: number;
  @Input() caseId!: number;
  @Input() activityId!: number;
  @Output() startedUploading = new EventEmitter<boolean>();
  @Output() uploadComplete = new EventEmitter<void>();
  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef;
  
  private uploadStatusSubscription!: Subscription; 
  private abortController: AbortController | null = null;

  pondOptions = {
    allowMultiple: false,
    acceptedFileTypes: ['video/mp4'],
    allowSyncAcceptAttribute: false,
    fileValidateTypeLabelExpectedTypes: 'Expects only video files!',
    labelFileTypeNotAllowed: 'Only MP4 files are allow.',
    labelIdle: 'Drag & Drop your files or <span class="filepond--label-action"> Browse </span><p class="m-0 fs-12 text-warning">** Support MP4 files only!</p>'
  };

  uploadForm!: FormGroup;
  videoSource: string | null = null;
  videoDuration = 0;
  currentTime = 0;

  startUpload = false;
  progress = 0;
  timeLeft = '';
  startTime!: number;
  
  speakers = [{ name: 'Single Speaker', value: 'y' }, { name: 'Multiple Speakers', value: 'n' }];

  constructor (private assessmentService: AssessmentService, private activityService: ActivityService, private authService: AuthService, private fb: FormBuilder) {}

  ngOnInit() {
    FilePond.registerPlugin(FilePondPluginFileValidateType);
    
    this.uploadForm = this.fb.group({
      speakerSelected: [null, Validators.required],
      baseline_start_time: [null],
      baseline_end_time: [null]
    });
    
    this.uploadForm.get('speakerSelected')?.valueChanges.subscribe(value => {
      const startControl = this.uploadForm.get('baseline_start_time');
      const endControl = this.uploadForm.get('baseline_end_time');
      
      if (value === 'n') { // Multiple speakers
        startControl?.setValidators(Validators.required);
        endControl?.setValidators(Validators.required);
      } else { // Single speaker
        startControl?.clearValidators();
        endControl?.clearValidators();
      }
      startControl?.updateValueAndValidity();
      endControl?.updateValueAndValidity();
    });
  }
  
  ngOnDestroy() {
    this.uploadStatusSubscription?.unsubscribe();
    this.abortController?.abort();
    
    if (this.myPond && this.myPond.pond) {
      this.myPond.pond.destroy();
    }
  }

  pondRemoveFile() {
    this.videoSource = null;
    if (this.videoElement) {
      this.videoElement.nativeElement.src = '';
    }
    this.uploadForm.reset();
  }

  pondHandleAddFile(event: any) {
    if (event.error) {
      if (this.myPond && this.myPond.pond) {
        this.myPond.pond.removeFile(event.file);
      }
      this.authService.toastErrorMessage(event.error.main, event.error.sub);
    } else {
      this.videoSource = URL.createObjectURL(event.file.file);
      const interval = setInterval(() => {
        if (this.videoElement) {
          clearInterval(interval);
          this.setupVideoElement(this.videoSource!);
        }
      }, 100);
    }
  }

  setupVideoElement(videoURL: string): void {
    const video: HTMLVideoElement = this.videoElement.nativeElement;

    video.onloadedmetadata = () => {
      this.videoDuration = video.duration / 60;
    };

    video.addEventListener('timeupdate', () => {
      this.currentTime = Math.floor(video.currentTime);
    });

    video.src = videoURL;
  }

  markTime(label: string) {
    if (label === 'start') {
      this.uploadForm.get('baseline_start_time')?.setValue(this.currentTime);
    } else {
      this.uploadForm.get('baseline_end_time')?.setValue(this.currentTime);
    }
  }

  async uploadFile() {
    this.uploadForm.markAllAsTouched();
    
    if (this.uploadForm.invalid) {
      return;
    }
    
    const formValue = this.uploadForm.value;

    if (formValue.speakerSelected === 'n') {
      if (formValue.baseline_start_time >= formValue.baseline_end_time) {
        this.authService.toastErrorMessage('Baseline start time must be before the end time.', 'Invalid Baseline');
        return;
      }
    }
    
    const filePondFile = this.myPond?.pond?.getFile();
    if (!filePondFile) {
      this.authService.toastErrorMessage('No file found.', 'Upload Error');
      return;
    }

    const data: any = {
      projectId: this.projectId,
      caseId: this.caseId,
      activityId: this.activityId,
      single_speaker: formValue.speakerSelected,
      video_length: this.videoDuration,
      baseline: {
        baseline_start: null,
        baseline_end: null,
        base_voice_start: formValue.baseline_start_time,
        base_voice_end: formValue.baseline_end_time
      }
    };
    
    this.startUpload = true;
    this.startedUploading.emit(true);
    this.abortController = new AbortController();

    this.uploadStatusSubscription = this.activityService.getUploadStatusSubject().subscribe((a) => {
      if (a === 'abort' || a === 'error') {
        this.startUpload = false;
        this.startedUploading.emit(false);
        this.pondRemoveFile();
        this.uploadStatusSubscription?.unsubscribe();
      }
      if (a === 'complete') {
        data.status = 'ANALYZING';
        this.uploadStatusSubscription?.unsubscribe();
        this.assessmentService.updateProjectCaseActivity(this.projectId, this.caseId, this.activityId, { activity_type: 'conversation' }, data).subscribe({
          next: () => this.uploadComplete.emit(),
          error: (err: any) => this.authService.toastErrorMessage(err.message, 'Updating Error')
        });
      }
    });

    const extension = this.getFileExtension(filePondFile.filename).toLowerCase();
    const fileName = `conversation_${this.activityId}.${extension}`;
    const selectedFile = new File([filePondFile.file], fileName, { type: filePondFile.fileType });
    const storage_route = `project_${this.projectId}/case_${this.caseId}/videos`;

    await this.activityService.uploadActivityFile(storage_route, selectedFile, this.onProgress.bind(this), this.abortController.signal);
  }

  cancelUpload() {
    this.abortController?.abort();
  }
  

  onProgress(loadedBytes: number, totalBytes: number) {
    // Calculate the progress percentage
    const progressPercentage = Math.floor((loadedBytes / totalBytes) * 100);
  
    // Update the progress bar or perform any other actions based on the progress
    this.progress = progressPercentage;
    this.timeLeft = this.calculateTimeLeft(loadedBytes, totalBytes);
  }

  calculateTimeLeft(loadedBytes: number, totalBytes: number): string {
    // Check if the upload is completed
    if (loadedBytes >= totalBytes) {
      return 'Done';
    }
  
    // Initialize the start time on the first progress update
    if (!this.startTime) {
      this.startTime = Date.now();
    }
  
    // Calculate the elapsed time since the start
    const elapsedTime = Date.now() - this.startTime;
  
    // Calculate the current speed (bytes per millisecond)
    const currentSpeed = loadedBytes / elapsedTime;
  
    // Estimate the time remaining (in milliseconds)
    const remainingTime = (totalBytes - loadedBytes) / currentSpeed;
  
    // Convert the remaining time to hours, minutes, and seconds
    const hours = Math.floor(remainingTime / (1000 * 60 * 60));
    const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);
  
    // Build the time left string based on hours, minutes, and seconds
    let timeLeft = 'Time left: ';

    if (hours > 0) {
      timeLeft += hours + 'h ';
    }

    if (minutes > 0) {
      timeLeft += minutes + 'm ';
    }

    timeLeft += seconds + 's';
  
    return timeLeft;
  }

  getFileExtension(filename: string): string {
    const parts = filename.split('.');
    if (parts.length === 1) {
      return '';
    }

    return parts[parts.length - 1];
  }

}
